/**
 * Keeps the appearance choice, the root element and every copy of the control agreeing.
 *
 * The palette itself is not this file's job. Dark is CSS — a `prefers-color-scheme` block for
 * System and an attribute selector for a manual override — which is what makes System work with
 * JavaScript disabled, follow the operating system live with no listener, and paint correctly on
 * the very first frame. The inline bootstrap in `snippets/timecard-appearance.liquid` sets the
 * attribute before paint; this file handles everything after that.
 *
 * So what is left here is small and entirely about state:
 *
 *   - write a choice to the root element and to storage
 *   - check the right radio in every control, including ones the theme editor just re-rendered
 *   - follow a change made in another tab
 *
 * The control shows two options, Light and Dark. System is not one of them and is still the
 * default: it is what an unconfigured visitor gets, what a previously stored `system` resolves to,
 * and what the stylesheet does with no attribute present. `effective()` is what lets a two-button
 * control stay honest about it — while nothing is stored, the button shown as current is whichever
 * one the operating system is currently producing.
 *
 * Storage may be unavailable. When it is, a choice still applies to the page in front of the
 * visitor and simply does not survive navigation. That is a real degradation and it is silent by
 * design: an error message about local storage is not something a shopper can act on.
 */

const KEY = 'timecard:appearance';
const MODES = ['system', 'light', 'dark'];

/**
 * Applies a mode to the document.
 *
 * `data-timecard-appearance` is the override the stylesheet reads, and it is REMOVED for System
 * rather than set to it — the media query has to be allowed to answer, and an attribute saying
 * 'system' would just be a third value every selector had to spell out.
 *
 * @param {string} mode
 */
function apply(mode) {
  const root = document.documentElement;

  if (mode === 'light' || mode === 'dark') {
    root.setAttribute('data-timecard-appearance', mode);
  } else {
    root.removeAttribute('data-timecard-appearance');
  }

  root.setAttribute('data-timecard-appearance-mode', mode);
}

/** The stored preference, or System for anything unrecognised — including a corrupted value. */
function stored() {
  try {
    const value = window.localStorage.getItem(KEY);
    return MODES.includes(value ?? '') ? /** @type {string} */ (value) : 'system';
  } catch {
    return 'system';
  }
}

/**
 * The appearance actually in effect, as one of the two the control can show.
 *
 * System is still how an unconfigured visitor starts, and it is still what a previously stored
 * `system` resolves to — but it no longer has a button, so it cannot be what a radio displays.
 * Resolving it against the operating system is what lets the control show one accurate answer
 * while following the OS automatically, rather than showing nothing checked or, worse, showing
 * Light on a dark screen.
 *
 * Reading the OS rather than the root element on purpose: while nothing is stored, the root
 * carries no override at all — that absence is exactly what lets the stylesheet's media query
 * answer — so the root cannot say which way the media query went.
 */
function effective() {
  /*
   * The root element first, and storage second.
   *
   * `apply()` writes the root whether or not `persist()` succeeded, so with storage blocked — a
   * private window, a quota failure, an embedded webview — the root is the only record that a
   * choice was made at all. Asking storage first would read back "nothing chosen" and re-check the
   * radio against the operating system, while the palette in front of the visitor stayed on the
   * choice they had just made.
   */
  const applied = document.documentElement.getAttribute('data-timecard-appearance-mode');
  if (applied === 'light' || applied === 'dark') return applied;

  const preference = stored();
  if (preference === 'light' || preference === 'dark') return preference;

  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    // No matchMedia is the same position as no preference: the stylesheet's default is light.
    return 'light';
  }
}

/** @param {string} mode */
function persist(mode) {
  try {
    if (mode === 'system') {
      // Removed rather than written, so a visitor who returns to System is back to the default
      // proper — including on a future visit where the default may itself have changed.
      window.localStorage.removeItem(KEY);
    } else {
      window.localStorage.setItem(KEY, mode);
    }
  } catch {
    /* Applied for this page either way. Nothing a shopper can do about blocked storage. */
  }
}

/**
 * Checks the radio matching the current mode in every control on the page.
 *
 * Each control is its own radio group, so this is a loop rather than one assignment. Controls
 * rendered by a section the editor just replaced are picked up by the same query.
 *
 * @param {string} mode
 */
function syncControls(mode) {
  for (const input of document.querySelectorAll('[data-timecard-appearance-option]')) {
    if (input instanceof HTMLInputElement) input.checked = input.value === mode;
  }
}

/**
 * Puts every control back in step with the root element.
 *
 * Exported because it is the whole of what this file guarantees: whatever the page has been
 * through, a visible control shows the appearance that is actually applied.
 */
export function init() {
  syncControls(effective());
}

/**
 * Records a choice: apply it, remember it, and put every control in step with it.
 *
 * @param {EventTarget | null} target
 */
function choose(target) {
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.hasAttribute('data-timecard-appearance-option')) return;
  if (!MODES.includes(target.value)) return;

  apply(target.value);
  persist(target.value);
  syncControls(target.value);
}

document.addEventListener('change', (event) => choose(event.target));

/**
 * The click that changes nothing, and has to count anyway.
 *
 * With nothing stored, the control shows the option the operating system is currently producing —
 * so on a light OS the Light radio is already checked. Someone who then clicks Light means "stop
 * following the OS and keep it light", and a radio that is already checked fires no `change`
 * event at all. Without this handler their choice would silently not be recorded, and the page
 * would flip to dark the next time their system did.
 *
 * `click` fires on every activation, including the ones that do change state, so the two handlers
 * overlap. `choose` is idempotent, which is what makes that harmless rather than something to
 * guard against.
 */
document.addEventListener('click', (event) => choose(event.target));

/**
 * A change made in another tab.
 *
 * The `storage` event fires only in the tabs that did NOT make the change, which is exactly the
 * set that needs telling. Without this, two open tabs disagree until one of them is reloaded.
 */
window.addEventListener('storage', (event) => {
  if (event.key !== KEY && event.key !== null) return;

  const mode = stored();
  apply(mode);
  syncControls(effective());
});

/**
 * The operating system changing while nobody has chosen.
 *
 * This is the case the control could not show before: with nothing stored, the palette follows the
 * OS through the stylesheet's media query and no script is involved — so the radios would keep
 * displaying whatever the OS said when the page loaded. Nothing is applied or persisted here; the
 * only thing that changes is which of the two is shown as current.
 *
 * A visitor who HAS chosen is unaffected, because `effective()` returns their stored choice and
 * the OS does not get a say.
 */
try {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', init);
} catch {
  /* No matchMedia: the control shows light, which is the stylesheet's default in that case too. */
}

/** The theme editor re-renders sections, and a re-rendered control comes back with nothing checked. */
document.addEventListener('shopify:section:load', init);

/**
 * After the browser has finished restoring form state.
 *
 * Browsers remember which radio in a group was checked and put it back on navigation — after
 * DOMContentLoaded, and after anything this file did there. Real mobile testing found exactly
 * that: the root element correctly carrying dark, the palette correctly dark, and all four
 * controls showing System, because the restoration had overwritten them with the server-rendered
 * default.
 *
 * `pageshow` fires after that restoration on both a fresh load and a bfcache return, which makes
 * it the one moment that is reliably late enough.
 */
window.addEventListener('pageshow', init);

/**
 * When a menu opens.
 *
 * The controls live inside the desktop dropdowns and the mobile drawer, so most of them are not
 * looked at until one of those is opened — and the header is the part of the page most likely to
 * have been re-rendered, hydrated or cloned in between. Syncing on the way in costs one attribute
 * write per radio and removes the whole class of "it was right when the page loaded" failures.
 *
 * Delegated and capturing: `toggle` does not bubble, and the drawers these fire from may not exist
 * when this runs.
 */
document.addEventListener('toggle', init, true);
document.addEventListener('dialog:open', init, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
